import {
  doiDefinition,
  landingPageUrlDefinition,
  languageCodesDefinition,
  orcidDefinition,
} from '../common/common-schemas';

/** XSD `relationship-type` tokens (`relations.xsd`). Used as form values as-is. */
export const grantRelationshipTypeEnum = [
  'finances',
  'continues',
  'isContinuedBy',
] as const;

export type GrantRelationshipType =
  (typeof grantRelationshipTypeEnum)[number];

export const isGrantRelationshipType = (
  type: string
): type is GrantRelationshipType =>
  grantRelationshipTypeEnum.some((allowed) => allowed === type);

const currencyEnum = [
  'AFA',
  'ALL',
  'DZD',
  'AON',
  'ARS',
  'AMD',
  'AWG',
  'AUD',
  'ATS',
  'AZM',
  'BSD',
  'BHD',
  'BDT',
  'BBD',
  'BYR',
  'BEF',
  'BZD',
  'BMD',
  'BTN',
  'BOB',
  'BAM',
  'BWP',
  'BRL',
  'BND',
  'BGL',
  'BIF',
  'KHR',
  'CAD',
  'CVE',
  'KYD',
  'XOF',
  'XAF',
  'XPF',
  'CLP',
  'CNY',
  'COP',
  'KMF',
  'CDF',
  'CRC',
  'HRK',
  'CUP',
  'CYP',
  'CZK',
  'DKK',
  'DEM',
  'DJF',
  'DOP',
  'NLG',
  'XCD',
  'ECS',
  'EGP',
  'SVC',
  'ERN',
  'EEK',
  'ETB',
  'EUR',
  'FKP',
  'FJD',
  'FIM',
  'FRF',
  'GMD',
  'GEL',
  'GHC',
  'GIP',
  'GRD',
  'GTQ',
  'GYD',
  'HTG',
  'HNL',
  'HKD',
  'HUF',
  'ISK',
  'INR',
  'IDR',
  'IRR',
  'IQD',
  'IEP',
  'ILS',
  'ITL',
  'JMD',
  'JPY',
  'JOD',
  'KZT',
  'KES',
  'KWD',
  'KGS',
  'LAK',
  'LVL',
  'LBP',
  'LSL',
  'LRD',
  'LYD',
  'LTL',
  'LUF',
  'MOP',
  'MKD',
  'MGF',
  'MWK',
  'MYR',
  'MVR',
  'MTL',
  'MRO',
  'MUR',
  'MXN',
  'MDL',
  'MNT',
  'MAD',
  'MZM',
  'MMK',
  'NAD',
  'NPR',
  'ANG',
  'ZRN',
  'NZD',
  'NIC',
  'NGN',
  'KPW',
  'NOK',
  'PKR',
  'PAB',
  'PGK',
  'PYG',
  'PEN',
  'PHP',
  'PLN',
  'PTE',
  'QAR',
  'OMR',
  'ROL',
  'RUR',
  'RWF',
  'STD',
  'SAR',
  'SCR',
  'SLL',
  'SGD',
  'SKK',
  'SIT',
  'SBD',
  'SOS',
  'ZAR',
  'KRW',
  'ESP',
  'LKR',
  'SHP',
  'GBP',
  'SDP',
  'SRG',
  'SZL',
  'SEK',
  'CHF',
  'SYP',
  'TWD',
  'TJR',
  'TZS',
  'THB',
  'TPE',
  'TOP',
  'TTD',
  'TND',
  'TRL',
  'TMM',
  'AED',
  'UGX',
  'UAH',
  'UYU',
  'USD',
  'UZS',
  'VUV',
  'VEB',
  'VND',
  'WST',
  'YER',
  'YUM',
  'ZMK',
  'ZWD',
];

const fundingTypeEnum = [
  'award',
  'contract',
  'crowdfunding',
  'endowment',
  'equipment',
  'facilities',
  'fellowship',
  'grant',
  'loan',
  'other',
  'prize',
  'salary-award',
  'secondment',
  'seed-funding',
  'training-grant',
];

const investigatorDefinition = {
  type: 'object',
  required: ['role'],
  properties: {
    givenName: {
      type: 'string',
      i18n: 'person.givenName',
    },
    familyName: {
      type: 'string',
      i18n: 'person.familyName',
    },
    orcid: {
      $ref: '#/definitions/orcid',
    },
    role: {
      type: 'string',
      i18n: 'grant_investigator_role',
      enum: [
        'lead_investigator',
        'co-lead_investigator',
        'investigator',
      ],
    },
    affiliation: {
      type: 'object',
      properties: {
        institution: {
          type: 'string',
          i18n: 'person.affiliation.institution',
          'x-rorIdField': 'ror',
        },
        ror: {
          type: 'string',
          i18n: 'person.affiliation.ror',
        },
      },
    },
  },
};

const commonDefinitions = {
  doi: doiDefinition,
  landingPageUrl: landingPageUrlDefinition,
  orcid: orcidDefinition,
  crossref_language_codes: languageCodesDefinition,
  investigator: investigatorDefinition,
};

export const grantSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  title: 'Grant Metadata',
  type: 'object',
  required: [
    'doi',
    'landingPageUrl',
    'award-number',
    'award-start-date',
    'project-title',
    'funding',
  ],
  properties: {
    doi: {
      ...doiDefinition,
      i18n: 'grants.doi_data.doi',
    },
    landingPageUrl: {
      ...landingPageUrlDefinition,
      i18n: 'grant_landingPageUrl',
    },
    'award-number': {
      type: 'string',
      i18n: 'award_number',
    },
    'award-start-date': {
      type: 'string',
      format: 'date',
      i18n: 'grants.award_start_date',
    },
    'project-title': {
      type: 'array',
      minItems: 1,
      default: [{}],
      i18n: 'grants.project.project-title',
      items: {
        type: 'object',
        required: ['title-text'],
        properties: {
          'title-text': {
            type: 'string',
            i18n: 'grants.project.project-title.title-text',
          },
          lang: {
            $ref: '#/definitions/crossref_language_codes',
          },
        },
      },
    },
    award_amount: {
      type: 'object',
      i18n: 'grant_award_amount',
      properties: {
        amount: {
          type: 'number',
          i18n: 'grants.project.award_amount.amount',
        },
        currency: {
          type: 'string',
          i18n: 'grants.project.award_amount.currency',
          enum: currencyEnum,
        },
      },
      if: {
        properties: {
          amount: { type: 'number' },
        },
        required: ['amount'],
      },
      then: {
        required: ['currency'],
      },
    },
    description: {
      type: 'array',
      default: [],
      i18n: 'grant_description',
      items: {
        type: 'object',
        properties: {
          'description-text': {
            type: 'string',
            i18n: 'grants.project.description.description-text',
          },
          lang: {
            $ref: '#/definitions/crossref_language_codes',
          },
        },
      },
    },
    funding: {
      type: 'object',
      i18n: 'grant_funding',
      required: ['funder-name', 'funding-type'],
      properties: {
        'funder-name': {
          type: 'string',
          i18n: 'grant_funding_funderName',
          'x-rorIdField': 'funderRorId',
        },
        funderRorId: {
          type: 'string',
          i18n: 'grant_funding_funderRorId',
          pattern: '^https://ror.org/*',
        },
        'funder-id': {
          type: 'string',
          i18n: 'grant_funding_funderId',
          pattern: '^https://doi\\.org/10\\.13039/(1|5)[0-9]{8,11}$',
        },
        'funding-type': {
          type: 'string',
          i18n: 'grants.project.funding.funding-type',
          enum: fundingTypeEnum,
        },
        'funding-scheme': {
          type: 'string',
          i18n: 'grants.project.funding.funding-scheme',
        },
      },
      if: {
        not: {
          required: ['funderRorId'],
          properties: {
            funderRorId: { type: 'string', minLength: 1 },
          },
        },
      },
      then: {
        required: ['funder-id'],
      },
    },
    relationships: {
      type: 'array',
      default: [],
      i18n: 'grant_relationships',
      items: {
        type: 'object',
        properties: {
          type_of_relationship: {
            type: 'string',
            enum: [...grantRelationshipTypeEnum],
            i18n: 'grant_relationships_type_of_relationship',
          },
          related_doi: {
            ...doiDefinition,
            i18n: 'grant_related_doi',
          },
        },
        dependencies: {
          type_of_relationship: ['related_doi'],
          related_doi: ['type_of_relationship'],
        },
      },
    },
    investigators: {
      type: 'array',
      default: [],
      i18n: 'grant_investigators',
      items: {
        $ref: '#/definitions/investigator',
      },
    },
  },
  definitions: {
    ...commonDefinitions,
  },
};

export const grantRecordSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  title: 'Grant',
  type: 'object',
  required: ['grantMetadata'],
  properties: {
    grantMetadata: grantSchema,
  },
  definitions: {
    ...commonDefinitions,
  },
};
